var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile, isTablet, isSafari } from 'react-device-detect';
import Video, { LocalDataTrack, } from 'twilio-video';
import { ensureMediaPermissions } from '../../../../../src/utils';
import { useAppState } from '../../../../../src/state';
import { useSafePromise } from '@alucio/lux-ui';
import { Logger } from '@aws-amplify/core';
const logger = new Logger('useLocalTracks Alucio', 'INFO');
export var MediaAccessState;
(function (MediaAccessState) {
    MediaAccessState[MediaAccessState["unknown"] = 0] = "unknown";
    MediaAccessState[MediaAccessState["prompted"] = 1] = "prompted";
    MediaAccessState[MediaAccessState["allowed"] = 2] = "allowed";
    MediaAccessState[MediaAccessState["denied"] = 3] = "denied";
    MediaAccessState[MediaAccessState["notAvailable"] = 4] = "notAvailable";
    MediaAccessState[MediaAccessState["notFound"] = 5] = "notFound";
})(MediaAccessState || (MediaAccessState = {}));
export var GumExceptions;
(function (GumExceptions) {
    GumExceptions[GumExceptions["unknown"] = 0] = "unknown";
    GumExceptions[GumExceptions["null"] = 1] = "null";
    GumExceptions[GumExceptions["notAllowed"] = 2] = "notAllowed";
    GumExceptions[GumExceptions["notAvailable"] = 3] = "notAvailable";
    GumExceptions[GumExceptions["notFound"] = 4] = "notFound";
})(GumExceptions || (GumExceptions = {}));
function getDefaultVideoOptions() {
    // In the DeviceSelector and FlipCameraButton components, a new video track is created,
    // then the old track is unpublished and the new track is published. Unpublishing the old
    // track and publishing the new track at the same time sometimes causes a conflict when the
    // track name is 'camera', so here we append a timestamp to the track name to avoid the
    // conflict.
    return {
        frameRate: 24,
        height: 144,
        width: 256,
        name: `camera-${Date.now()}`,
    };
}
export default function useLocalTracks() {
    const [audioTrack, setAudioTrack] = useState();
    const [videoTrack, setVideoTrack] = useState();
    const [dataTrack, setDataTrack] = useState();
    const [videoDeviceOptions, setVideoDeviceOptions] = useState({});
    const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = useState(false);
    const [audioAccessState, setAudioAccessState] = useState(MediaAccessState.unknown);
    const [videoAccessState, setVideoAccessState] = useState(MediaAccessState.unknown);
    const [gumExceptionState, setGumExceptionState] = useState(GumExceptions.null);
    const { setActiveSinkId } = useAppState();
    const safePromise = useSafePromise();
    // Using this var to detect when we allowed media while running a periodic timeout
    // Info: https://github.com/facebook/react/issues/14010
    const previousAccessStateValues = useRef({ audioAccessState, videoAccessState });
    let allowedAction;
    const getLocalAudioTrack = useCallback((deviceId) => {
        const options = {};
        if (isMobile || isTablet) {
            throw new Error('Get Audio Track not supported in mobile');
        }
        if (deviceId) {
            options.deviceId = { exact: deviceId };
        }
        return Video.createLocalAudioTrack(options).then(newTrack => {
            setAudioTrack(newTrack);
            return newTrack;
        });
    }, []);
    const getLocalVideoTrack = useCallback((newOptions) => {
        logger.debug('Acquiring Video Track');
        if (isMobile || isTablet) {
            throw new Error('Get Audio Track not supported in mobile');
        }
        // Save device selection if provided for when we stop and restart the video (which causes a republish)
        if (newOptions) {
            setVideoDeviceOptions(newOptions);
        }
        const options = Object.assign(Object.assign({}, getDefaultVideoOptions()), (newOptions !== null && newOptions !== void 0 ? newOptions : videoDeviceOptions));
        return Video.createLocalVideoTrack(options).then(newTrack => {
            setVideoTrack(newTrack);
            return newTrack;
        }).catch(() => {
            // Try with default video options in case specified deviceid is invalid (device unplugged)
            return Video.createLocalVideoTrack(getDefaultVideoOptions()).then(newTrack => {
                setVideoTrack(newTrack);
                return newTrack;
            });
        });
    }, [videoDeviceOptions]);
    const removeLocalVideoTrack = useCallback(() => {
        logger.debug('removing Video Track', videoTrack === null || videoTrack === void 0 ? void 0 : videoTrack.id);
        if (videoTrack) {
            videoTrack.stop();
            videoTrack.detach();
            setVideoTrack(undefined);
        }
    }, [videoTrack]);
    const removeLocalAudioTrack = useCallback(() => {
        logger.debug('removing Audio Track', audioTrack === null || audioTrack === void 0 ? void 0 : audioTrack.id);
        if (audioTrack) {
            audioTrack.stop();
            audioTrack.detach();
            setAudioTrack(undefined);
        }
    }, [audioTrack]);
    const getLocalDataTrack = useCallback((newOptions) => {
        const options = Object.assign({ logLevel: 'info', maxPacketLifeTime: 1000 }, newOptions);
        const dataTrackPromise = new Promise((resolve) => {
            resolve(new LocalDataTrack());
        }).then(newTrack => {
            options;
            setDataTrack(newTrack);
            return newTrack;
        });
        return dataTrackPromise;
    }, []);
    const getDefaultDevices = () => __awaiter(this, void 0, void 0, function* () {
        const defaultAudioOutputDeviceId = localStorage.getItem('beacon-default-audiooutput');
        const defaultAudioInputDeviceId = localStorage.getItem('beacon-default-audioinput');
        const defaultVideoDeviceId = localStorage.getItem('beacon-default-videoinput');
        try {
            yield safePromise.makeSafe(ensureMediaPermissions());
            const devices = yield safePromise.makeSafe(navigator.mediaDevices.enumerateDevices());
            let defaultAudioInputDevice;
            let defaultAudioOutputDevice;
            let defaultVideoDevice;
            devices.forEach((device) => {
                if (device.deviceId === defaultAudioInputDeviceId && device.kind === 'audioinput') {
                    defaultAudioInputDevice = device;
                }
                else if (device.deviceId === defaultVideoDeviceId && device.kind === 'videoinput') {
                    defaultVideoDevice = device;
                }
                else if (device.deviceId === defaultAudioOutputDeviceId && device.kind === 'audiooutput') {
                    defaultAudioOutputDevice = device;
                }
            });
            if (defaultAudioOutputDevice) {
                setActiveSinkId(defaultAudioOutputDevice.deviceId);
            }
            return {
                defaultAudioInputDevice,
                defaultAudioOutputDevice,
                defaultVideoDevice,
            };
        }
        catch (err) {
            logger.debug('Exception occured when fetching default Devices', err);
            if (!err.isCanceled)
                throw err;
        }
        return {
            defaultVideoDevice: undefined,
            defaultAudioInputDevice: undefined,
            defaultAudioOutputDevice: undefined,
        };
    });
    const checkPermissions = () => __awaiter(this, void 0, void 0, function* () {
        // This recent API (https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API)
        // lets us check state before requesting media but is not yet supported in Safari
        logger.debug('Checking permissions');
        const [{ state: camState }, { state: micState }] = yield Promise.all([
            navigator.permissions.query({ name: 'camera' }),
            navigator.permissions.query({ name: 'microphone' }),
        ]);
        let videoAccessStateVar = MediaAccessState.unknown;
        let audioAccessStateVar = MediaAccessState.unknown;
        if (camState === 'granted') {
            videoAccessStateVar = MediaAccessState.allowed;
        }
        else if (camState === 'denied') {
            videoAccessStateVar = MediaAccessState.denied;
        }
        else if (camState === 'prompt') {
            videoAccessStateVar = MediaAccessState.prompted;
        }
        if (micState === 'granted') {
            audioAccessStateVar = MediaAccessState.allowed;
        }
        else if (micState === 'denied') {
            audioAccessStateVar = MediaAccessState.denied;
        }
        else if (micState === 'prompt') {
            audioAccessStateVar = MediaAccessState.prompted;
        }
        if (previousAccessStateValues.current.audioAccessState !== audioAccessStateVar ||
            previousAccessStateValues.current.videoAccessState !== videoAccessStateVar) {
            if ((previousAccessStateValues.current.audioAccessState === MediaAccessState.denied ||
                previousAccessStateValues.current.videoAccessState === MediaAccessState.denied) &&
                (audioAccessStateVar === MediaAccessState.allowed || videoAccessStateVar === MediaAccessState.allowed)) {
                allowedAction = true;
            }
            const changedAudio = previousAccessStateValues.current.audioAccessState !== audioAccessStateVar;
            const changedVideo = previousAccessStateValues.current.videoAccessState !== videoAccessStateVar;
            previousAccessStateValues.current = {
                audioAccessState: audioAccessStateVar,
                videoAccessState: videoAccessStateVar,
            };
            if (changedAudio)
                setAudioAccessState(previousAccessStateValues.current.audioAccessState);
            if (changedVideo)
                setVideoAccessState(previousAccessStateValues.current.videoAccessState);
        }
    });
    const getLocalTrackOptions = (defaultDevices) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const isAudioEnabled = !(isMobile || isTablet);
        const devices = yield navigator.mediaDevices.enumerateDevices();
        const videoInput = (_a = defaultDevices.defaultVideoDevice) !== null && _a !== void 0 ? _a : devices.find(device => device.kind === 'videoinput');
        const audioInput = (_b = defaultDevices.defaultAudioInputDevice) !== null && _b !== void 0 ? _b : devices.find(device => device.kind === 'audioinput');
        const video = !videoInput ? false
            : Object.assign(Object.assign({}, getDefaultVideoOptions()), { deviceId: { exact: videoInput.deviceId } });
        const audio = !audioInput || !isAudioEnabled ? false
            : {
                deviceId: { exact: audioInput.deviceId },
            };
        return {
            video,
            audio,
        };
    });
    const handleCreateLocalTracksCallback = (tracks) => {
        const videoTrack = tracks.find(track => track.kind === 'video');
        logger.debug('createLocalTracks Callback:', tracks);
        const audioTrack = tracks.find(track => track.kind === 'audio');
        const dataTrack = tracks.find(track => track.kind === 'data');
        if (videoTrack) {
            // const vidTrack = videoTrack as LocalVideoTrack
            setVideoTrack(videoTrack);
            // [TODO]: This a not great workaround; the video track does not provide the video device id, only the track id
            //  - Generally, the existing code should continue to return fail when checking against this id (since it's expecting a device id) not a label
            //  - However, this works in our favor in the DeviceSettings component since it checks for `deviceId.exact` to be a video device id or label
            //  - Ideally, we'd set the video device id here, but we'd have get a list of currently available devices, which is possible
            //    but complicates this code and deviates from src a bit further
            // setVideoDeviceOptions({ deviceId: { exact: vidTrack.mediaStreamTrack.label } })
            setVideoAccessState(MediaAccessState.allowed);
        }
        if (audioTrack) {
            setAudioTrack(audioTrack);
            setAudioAccessState(MediaAccessState.allowed);
        }
        if (dataTrack) {
            setDataTrack(dataTrack);
        }
    };
    const createLocalTracks = (defaultDevices) => __awaiter(this, void 0, void 0, function* () {
        logger.debug('Starting retrieval of local tracks');
        try {
            const { audio, video } = yield getLocalTrackOptions(defaultDevices);
            if (!video)
                setVideoAccessState(MediaAccessState.notFound);
            if (!audio)
                setAudioAccessState(MediaAccessState.notFound);
            Video.createLocalTracks({ audio, video })
                .then(tracks => {
                handleCreateLocalTracksCallback(tracks);
                setGumExceptionState(GumExceptions.null);
            })
                .catch(e => {
                if (e.name === 'NotReadableError' || e.name === 'AbortError') {
                    setGumExceptionState(GumExceptions.notAvailable);
                }
                else if (e.name === 'NotFoundError') {
                    setGumExceptionState(GumExceptions.notFound);
                }
                else if (e.name === 'NotAllowedError') {
                    setGumExceptionState(GumExceptions.notAllowed);
                }
                console.error(`Create local track error: ${e}`);
            });
        }
        catch (e) {
            logger.warn('Error retrieving media device settings', e);
        }
        setIsAcquiringLocalTracks(false);
    });
    useEffect(() => {
        const init = () => __awaiter(this, void 0, void 0, function* () {
            logger.debug('starting init of local tracks');
            if (!isMobile && !isTablet) {
                const defaultDevices = yield getDefaultDevices();
                logger.debug('Retrieved default devices', defaultDevices);
                if (isSafari) {
                    setIsAcquiringLocalTracks(true);
                    yield createLocalTracks(defaultDevices);
                }
                else {
                    yield checkPermissions();
                    setIsAcquiringLocalTracks(true);
                    yield createLocalTracks(defaultDevices);
                    setIsAcquiringLocalTracks(false);
                }
            }
            logger.debug('done init of local tracks');
        });
        init();
    }, []);
    useEffect(() => {
        const run = () => __awaiter(this, void 0, void 0, function* () {
            const tryAudio = !(isMobile || isTablet);
            if (!tryAudio || audioAccessState === MediaAccessState.notFound) {
                return;
            }
            //  If we have an exception we will try if audio only works first
            if ((gumExceptionState === GumExceptions.notAllowed || gumExceptionState === GumExceptions.notAvailable)) {
                const { audio, video } = yield getLocalTrackOptions(yield getDefaultDevices());
                Video.createLocalTracks({ audio: audio, video: false })
                    .then(tracks => {
                    const audioTrack = tracks.find(track => track.kind === 'audio');
                    const dataTrack = tracks.find(track => track.kind === 'data');
                    if (gumExceptionState === GumExceptions.notAllowed) {
                        setVideoAccessState(MediaAccessState.denied);
                    }
                    else if (gumExceptionState === GumExceptions.notAvailable) {
                        setVideoAccessState(MediaAccessState.notAvailable);
                    }
                    if (audioTrack) {
                        setAudioTrack(audioTrack);
                        setAudioAccessState(MediaAccessState.allowed);
                    }
                    if (dataTrack) {
                        setDataTrack(dataTrack);
                    }
                    setGumExceptionState(GumExceptions.null);
                }).catch(e => {
                    if (e.name === 'NotReadableError' || e.name === 'AbortError') {
                        setAudioAccessState(MediaAccessState.notAvailable);
                        setVideoAccessState(MediaAccessState.notAvailable);
                    }
                    else if (e.name === 'NotAllowedError') {
                        setAudioAccessState(MediaAccessState.denied);
                        setVideoAccessState(MediaAccessState.denied);
                    }
                    else {
                        // we pass this for letting the user join even
                        // there is some unknown error that will prevent using audio and video
                        setAudioAccessState(MediaAccessState.allowed);
                        setVideoAccessState(MediaAccessState.notAvailable);
                    }
                    // we do this to request again audio and video together, not only audio
                    Video.createLocalTracks({ audio: audio, video: video })
                        .then((tracks) => handleCreateLocalTracksCallback(tracks)).catch((e) => {
                        logger.error(`Video/Audio access error: ${e}`);
                    });
                });
            }
        });
        run();
    }, [gumExceptionState]);
    // Polling to detect changes in media access state
    useEffect(() => {
        if (!isMobile && !isTablet) {
            let timerId;
            const check = () => __awaiter(this, void 0, void 0, function* () {
                checkPermissions();
                if (previousAccessStateValues.current.audioAccessState === MediaAccessState.allowed &&
                    previousAccessStateValues.current.videoAccessState === MediaAccessState.allowed && allowedAction) {
                    const defaultDevices = yield getDefaultDevices();
                    setIsAcquiringLocalTracks(true);
                    yield createLocalTracks(defaultDevices);
                    setIsAcquiringLocalTracks(false);
                    allowedAction = false;
                }
                timerId = window.setTimeout(check, 3000);
            });
            check();
            return () => {
                if (timerId) {
                    clearTimeout(timerId);
                }
            };
        }
    }, []);
    const localTracks = [audioTrack, videoTrack, dataTrack].filter(track => track !== undefined);
    return {
        localTracks,
        getLocalVideoTrack,
        getLocalAudioTrack,
        getLocalDataTrack,
        isAcquiringLocalTracks,
        removeLocalVideoTrack,
        removeLocalAudioTrack,
        videoDeviceOptions,
        setVideoDeviceOptions,
        audioAccessState,
        videoAccessState,
        gumExceptionState,
    };
}
//# sourceMappingURL=useLocalTracks.js.map